import { AxiosError } from 'axios';
import SettingsDirectAccess from 'store/settings/directAccess';
import {
  ProcessingJob,
  ProcessingJobAbstract,
  ProcessingJobRequest,
  RiskAssessmentProcessingJobRequest,
  DefinedInputs,
} from 'types/proccesingJobs';
import { HcsAPI } from '..';
import formatAxiosError from '../api-error-handler/ApiErrorHandler';
import mapJob from './mapper';

class ProcessingJobRepository {
  async createGroupProcess(
    documentGroupId: string,
    workflowId: string,
    externalSourceData: string | undefined,
    documentIdsToExtract: string[] | undefined,
  ): Promise<ProcessingJob> {
    try {
      const definedInputs: DefinedInputs = {
        DocumentGroupId: documentGroupId,
      };

      let parsedExternalSourceData;
      if (externalSourceData !== undefined && externalSourceData !== '') {
        parsedExternalSourceData = JSON.parse(externalSourceData);
      }

      if (parsedExternalSourceData) {
        definedInputs.ExternalSourceData = parsedExternalSourceData;
      }

      if (documentIdsToExtract && documentIdsToExtract.length > 0) {
        definedInputs.DocumentIdsToExtract = documentIdsToExtract;
      }

      const request: ProcessingJobRequest = {
        workflowId,
        definedInputs,
      };
      const formData = new FormData();
      formData.append('request', JSON.stringify(request));
      const { data } = await HcsAPI.post<ProcessingJobAbstract>('processingJobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return mapJob(data);
    } catch (error) {
      throw formatAxiosError(
        error as AxiosError,
        'An error has occured while creating processing job.',
      );
    }
  }

  async create(
    file: File,
    definedInputJson: string,
    inputFileConfigurations?: string,
  ): Promise<ProcessingJob> {
    let parsedDefinedInput;
    if (definedInputJson !== '') {
      parsedDefinedInput = JSON.parse(definedInputJson);
    }

    let parsedInputFileConfigurations;
    if (inputFileConfigurations && inputFileConfigurations !== '') {
      parsedInputFileConfigurations = JSON.parse(inputFileConfigurations);
    }

    const request: ProcessingJobRequest = {
      workflowId: SettingsDirectAccess.getSettings().workflowId,
      definedInputs: parsedDefinedInput,
    };

    if (parsedInputFileConfigurations) {
      request.inputFileConfigurations = parsedInputFileConfigurations;
    }

    const formData = new FormData();
    formData.append('request', JSON.stringify(request));
    formData.append('File', file);
    try {
      const { data } = await HcsAPI.post<ProcessingJobAbstract>('processingJobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return mapJob(data);
    } catch (error) {
      throw formatAxiosError(
        error as AxiosError,
        'An error has occured while creating processing job.',
      );
    }
  }

  async createSearch(name?: string): Promise<ProcessingJob> {
    const personNameInfo = {
      PersonName: name,
    };
    const request: RiskAssessmentProcessingJobRequest = {
      workflowId: SettingsDirectAccess.getSettings().workflowId,
      definedInputs: personNameInfo,
    };

    const formData = new FormData();
    formData.append('request', JSON.stringify(request));
    try {
      const { data } = await HcsAPI.post<ProcessingJobAbstract>('processingJobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return mapJob(data);
    } catch (error) {
      throw formatAxiosError(
        error as AxiosError,
        'An error has occured while creating processing job.',
      );
    }
  }

  async get(processingJobId: string): Promise<ProcessingJob> {
    try {
      const { data } = await HcsAPI.get<ProcessingJobAbstract>(`processingJobs/${processingJobId}`);
      return mapJob(data);
    } catch (error) {
      throw formatAxiosError(
        error as AxiosError,
        'An error has occured while retrieving processing job.',
      );
    }
  }
}

export default new ProcessingJobRepository();
